import { EyeOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { Space, Tooltip, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { IngredientTableRow, Pagination } from '@app/models/feed_exp_ingredients/IngredientResponse';
import { getIngredientData } from '@app/api/feedExpIngredients.api';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export const ExpectedIngredientsTable: React.FC = () => {
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [tableData, setTableData] = useState<{ data: IngredientTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const hasFetchedRef = useRef(false);

  const fetch = useCallback((pagination = { current: 1, pageSize: 10 }) => {
    setTableData((prev) => ({ ...prev, loading: true }));

    getIngredientData(pagination)
      .then((res) => {
        setTableData({
          data: res.data || [],
          pagination: res.pagination,
          loading: false,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, []);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetch();
    }
  }, [fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      ...filters,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-exp-ingredient`, { state: { id } });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Feed Expected Ingredients`}</span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
  }[] = [
    {
      name: 'Year',
      placeholder: 'year',
      type: 'text',
    },
  ];

  const columns: ColumnsType<IngredientTableRow> = [
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];
  return (
    <BaseCard id="basic-table" title="Feed Expected Ingredients" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
